<template>
  <div class="mt-16 flex items-center justify-center typo-label-lg">
    <button
      type="button"
      class="mr-4 size-[1.375em] transition-colors"
      :disabled="page <= 1"
      :aria-label="$t('aria_labels.go_to_previous_page')"
      @click="paginate(page - 1)"
    >
      <IconArrowDown
        class="rotate-90 transition-opacity"
        :class="{ 'opacity-20': page <= 1 }"
      />
    </button>

    <div class="relative">
      <!-- Floating Background -->
      <div
        ref="floatingBackground"
        class="
          absolute -bottom-1 mt-auto h-0.5 rounded-full bg-blue-500 transition-[left,width,bottom]
          duration-300
        "
        :class="page >= 10 ? 'w-[1.25em]' : 'w-[1em]'"
        :style="{ left: `${backgroundLeft}px`, bottom: `${backgroundBottom}px` }"
      />

      <!-- Buttons -->
      <ul class="relative flex flex-wrap">
        <li v-for="index in totalPages" :key="index" class="flex w-fit justify-center px-4">
          <button
            :id="`pagination-button-${index}`"
            type="button"
            :aria-label="`${$t('aria_labels.go_to_page')} ${index + 1}`"
            class=""
            @click="paginate(index)"
          >
            <span :class="{ 'text-blue-500': page === index }">
              {{ index }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <button
      type="button"
      class="ml-4 size-[1.375em] transition-colors"
      :disabled="page >= totalPages"
      :aria-label="$t('aria_labels.go_to_next_page')"
      @click="paginate(page + 1)"
    >
      <IconArrowDown
        class="-rotate-90 transition-opacity"
        :class="{ 'opacity-20': page >= totalPages }"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  page: {
    type: Number,
    default: 1,
  },
  totalPages: {
    type: Number,
    required: true,
  },

})

const floatingBackground = ref<HTMLElement | null>(null)
const backgroundLeft = ref<number>(0)
const backgroundBottom = ref<number>(0)

function paginate(index: number) {
  updateFloatingBackground(index)
  emit('paginate', index)
}

async function updateFloatingBackground(index: number) {
  await nextTick()

  const activeButton = document.getElementById(`pagination-button-${index}`)
  if (activeButton && floatingBackground.value) {
    const buttonRect = activeButton.getBoundingClientRect()
    const parentRect = floatingBackground.value.parentElement?.getBoundingClientRect()
    if (parentRect) {
      const leftPosition = buttonRect.left - parentRect.left - 4
      backgroundLeft.value = leftPosition
      const bottomPosition = parentRect.bottom - buttonRect.bottom - 2
      backgroundBottom.value = bottomPosition
    }
  }
}

onMounted(() => {
  // Initialize the background position
  updateFloatingBackground(props.page)
})

const emit = defineEmits(['paginate'])
</script>
